import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthenticatedFetch } from '../utils/api';
import { showSuccessToast, showErrorToast } from '../utils/toast';

function PricingPage() {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const fetchRef = useRef(authenticatedFetch);
  useEffect(() => { fetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  const [packages, setPackages]     = useState([]);
  const [balance, setBalance]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [error, setError]           = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pkgsRes, balRes] = await Promise.all([
        fetchRef.current('/api/credits/packages'),
        fetchRef.current('/api/credits/balance'),
      ]);
      if (pkgsRes?.ok) {
        const d = await pkgsRes.json();
        if (d.success) setPackages(d.data || []);
      }
      if (balRes?.ok) {
        const d = await balRes.json();
        if (d.success) setBalance(d.data);
      }
    } catch {
      setError('Failed to load pricing information. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [declinedBanner, setDeclinedBanner] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('credits_added') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => showSuccessToast('Credits added to your account!'), 400);
      fetchData();
    }
    if (params.get('credits_declined') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      setDeclinedBanner(true);
    }
  }, [fetchData]);

  const handlePurchase = async (packageId) => {
    setPurchasing(packageId);
    setError(null);
    try {
      // Include host so the billing callback can redirect back to the embedded app correctly
      const host = new URLSearchParams(window.location.search).get('host');
      const res = await fetchRef.current('/api/credits/purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packageId, host }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to initiate purchase');
      window.top.location.href = data.data.confirmationUrl;
    } catch (err) {
      showErrorToast(err.message || 'Purchase failed.');
      setError(err.message);
    } finally {
      setPurchasing(null);
    }
  };

  const { usedCredits = 0, totalCredits = 0, remainingCredits = 0 } = balance || {};
  const isEmpty     = remainingCredits <= 0;
  const isLow       = !isEmpty && totalCredits > 0 && (remainingCredits / totalCredits) <= 0.20;

  // Usage percentage (0% = new, 100% = empty)
  const usagePct    = totalCredits > 0 ? Math.min(100, Math.round((usedCredits / totalCredits) * 100)) : 0;
  const barColor    = isEmpty ? '#e0211d' : isLow ? '#e6a817' : '#36a420';

  return (
    <s-section>
      <s-stack gap="large">

        {/* Header */}
        <s-stack gap="small">
          <s-heading>Credits &amp; Pricing</s-heading>
          <s-text tone="subdued">
            Buy image optimization credits — one credit per image, no monthly fees, no expiry.
          </s-text>
        </s-stack>

        {/* Balance card */}
        {!loading && balance && (
          <s-box padding="base" border="base" borderRadius="base" background="subdued">
            <s-stack gap="small">
              <s-stack direction="inline" justifyContent="space-between" alignItems="center">
                <s-stack gap="none">
                  <s-text size="small" tone="subdued">Your current usage</s-text>
                  <s-stack direction="inline" gap="small" alignItems="baseline">
                    <s-heading size="large">{usedCredits.toLocaleString()}</s-heading>
                    <s-text tone="subdued" size="small">/ {totalCredits.toLocaleString()} credits</s-text>
                  </s-stack>
                </s-stack>
                <s-stack gap="none" alignItems="end">
                  <s-text size="small" tone="subdued">Remaining</s-text>
                  <s-text type="strong">{remainingCredits.toLocaleString()}</s-text>
                </s-stack>
              </s-stack>
              {/* Progress bar: fill = used credits (from left to right) */}
              <div style={{ height: 8, borderRadius: 4, background: 'rgba(0,0,0,0.1)', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.05)' }}>
                <div style={{ height: '100%', width: `${usagePct}%`, background: barColor, borderRadius: 4, transition: 'width 0.3s ease' }} />
              </div>
              <s-text size="small" tone="subdued">{usagePct}% used — {usedCredits.toLocaleString()} used out of {totalCredits.toLocaleString()}</s-text>
            </s-stack>
          </s-box>
        )}

        {declinedBanner && (
          <s-banner tone="warning" onDismiss={() => setDeclinedBanner(false)}>
            Your credit purchase was cancelled. Choose a package below to try again.
          </s-banner>
        )}

        {error && <s-banner tone="critical" onDismiss={() => setError(null)}>{error}</s-banner>}

        {/* Credit info pill */}
        <s-stack direction="inline" gap="small" alignItems="center">
          <s-badge tone="info">1 credit = 1 image optimized</s-badge>
          <s-badge>Credits never expire</s-badge>
        </s-stack>

        {/* Packages grid */}
        <s-grid gridTemplateColumns="repeat(auto-fit, minmax(260px, 1fr))" gap="base">
          {loading ? (
            [0, 1, 2].map(i => (
              <div key={i} className="shimmer-loader" style={{ height: 280, borderRadius: 8 }} />
            ))
          ) : (
            packages.map((pkg) => {
              const isFree       = !pkg.isPurchasable;
              const isPopular    = pkg.isPopular;
              const isPurchasing = purchasing === pkg.packageId;
              const features     = pkg.features || [];

              return (
                <s-box
                  key={pkg.packageId}
                  padding="base"
                  border="base"
                  borderRadius="base"
                  background={isPopular ? 'subdued' : 'base'}
                  style={isPopular ? { borderColor: 'var(--p-color-border-emphasis,#005bd3)', borderWidth: '2px' } : {}}
                >
                  <s-stack gap="base">

                    {/* Plan name + badge row */}
                    <s-stack direction="inline" justifyContent="space-between" alignItems="center">
                      <s-text tone="subdued" size="small" type="strong" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {pkg.name}
                      </s-text>
                      {isPopular && <s-badge tone="info">Most Popular</s-badge>}
                      {isFree    && <s-badge>Free</s-badge>}
                    </s-stack>

                    {/* Price — large, prominent */}
                    <s-stack gap="none">
                      <s-heading size="large">
                        {pkg.price === 0 ? 'Free' : `$${Number(pkg.price).toFixed(2)}`}
                      </s-heading>
                      <s-text size="small" tone="subdued">
                        {isFree ? 'Included with first install' : 'One-time · No expiry'}
                      </s-text>
                    </s-stack>

                    {/* Credits count */}
                    <s-box padding="small" background="subdued" borderRadius="base">
                      <s-stack direction="inline" gap="small" alignItems="center">
                        <s-heading size="small">{pkg.credits.toLocaleString()}</s-heading>
                        <s-text tone="subdued">image credits</s-text>
                      </s-stack>
                    </s-box>

                    <s-divider />

                    {/* Features */}
                    {features.length > 0 && (
                      <s-stack gap="small">
                        {features.map((f, i) => (
                          <s-stack key={i} direction="inline" gap="small" alignItems="start">
                            <s-icon source="check-circle" tone="success" />
                            <s-text size="small">{f}</s-text>
                          </s-stack>
                        ))}
                      </s-stack>
                    )}

                    {/* CTA */}
                    {isFree ? (
                      <s-button variant="secondary" disabled>Included free</s-button>
                    ) : (
                      <s-button
                        variant={isPopular ? 'primary' : 'secondary'}
                        disabled={isPurchasing}
                        loading={isPurchasing}
                        onClick={() => handlePurchase(pkg.packageId)}
                      >
                        {isPurchasing ? 'Redirecting…' : `Buy ${pkg.credits.toLocaleString()} credits — $${Number(pkg.price).toFixed(2)}`}
                      </s-button>
                    )}

                  </s-stack>
                </s-box>
              );
            })
          )}
        </s-grid>

        {/* Fine print */}
        <s-text size="small" tone="subdued">
          All purchases are one-time charges processed through Shopify. Credits never expire and are applied immediately after approval.
          Credits are consumed per image optimized (1 credit = 1 image) and are not refunded when an image is reverted.
        </s-text>

      </s-stack>
    </s-section>
  );
}

export default PricingPage;
